import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { useWindowSize } from '@/shared/hooks/useWindowSize';

interface ChestRewardModalProps {
  isOpen: boolean;
  rewards: number;
  onClose: () => void;
}

export const ChestRewardModal = ({
  isOpen,
  rewards,
  onClose,
}: ChestRewardModalProps) => {
  const { width, height } = useWindowSize();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-[120]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal Container */}
          <div className="fixed left-0 right-0 top-1/2 -translate-y-1/2 z-[121] max-w-[var(--app-max-width)] mx-auto flex justify-center items-center pointer-events-none">
            <motion.div
              className="relative w-[339px] flex flex-col items-center p-6 isolate bg-gradient-to-b from-[#FFD700] via-[#FFA500] to-[#FF8C00] border-4 border-yellow-300 rounded-3xl pointer-events-auto shadow-2xl"
              initial={{ opacity: 0, scale: 0.5, y: 100 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.5, y: 100 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            >
              {/* Shine effect */}
              <div className="absolute inset-x-4 top-4 h-20 bg-white/30 rounded-full blur-xl" />

              {/* Content */}
              <div className="relative flex flex-col items-center gap-6 w-full z-10">
                {/* Celebration Icon */}
                <motion.div
                  className="text-8xl"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                >
                  🎉
                </motion.div>

                {/* Title */}
                <motion.h1
                  className="font-lilita text-4xl text-white text-center"
                  style={{
                    textShadow: '2px 3px 0px #000000',
                    WebkitTextStroke: '2px #000',
                  }}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  Congratulations!
                </motion.h1>

                {/* Reward Amount */}
                <motion.div
                  className="flex flex-col items-center gap-2 py-4 px-8 bg-white/20 rounded-2xl border-2 border-white/50 backdrop-blur-sm"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring' }}
                >
                  <p
                    className="font-lilita text-xl text-white"
                    style={{
                      textShadow: '1px 2px 0px #000000',
                    }}
                  >
                    You received
                  </p>
                  <div className="flex items-center gap-2">
                    <motion.p
                      className="font-lilita text-5xl text-yellow-300"
                      style={{
                        textShadow: '2px 3px 0px #000000',
                        WebkitTextStroke: '1.5px #000',
                      }}
                      initial={{ scale: 0 }}
                      animate={{ scale: [0, 1.3, 1] }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                    >
                      {rewards.toLocaleString()}
                    </motion.p>
                    <motion.span
                      className="font-lilita text-3xl text-white"
                      style={{
                        textShadow: '1px 2px 0px #000000',
                      }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 }}
                    >
                      Gold
                    </motion.span>
                  </div>
                </motion.div>

                {/* Close Button */}
                <motion.button
                  onClick={onClose}
                  className="relative flex items-center justify-center px-8 py-3 gap-3 isolate border-3 border-black rounded-xl overflow-hidden mt-2"
                  style={{
                    background:
                      'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                    minWidth: '200px',
                  }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <div className="absolute inset-x-2 top-2 h-7 bg-white/30 rounded-lg z-0" />
                  <span
                    className="font-lilita text-3xl leading-8 text-white relative z-10"
                    style={{
                      textShadow: '1px 2px 0px #000000',
                      WebkitTextStroke: '1px #000',
                    }}
                  >
                    Claim
                  </span>
                </motion.button>
              </div>
            </motion.div>
          </div>

          {/* Confetti - Highest z-index to appear on top */}
          <div className="fixed inset-0 z-[125] pointer-events-none">
            <Confetti
              width={width}
              height={height}
              recycle={false}
              numberOfPieces={500}
              gravity={0.3}
            />
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
